import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listRoutes } from '@/api/endpoints/routes';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { RouteListFilters, RouteListSort, RouteResponse } from '@/types/routes';
import { extractAxiosMessage } from '@/utils/axiosError';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export const ROUTES_LIBRARY_PAGE_SIZE = DEFAULT_PAGE_SIZE;

export const EMPTY_ROUTE_FILTERS: RouteListFilters = {
  periodFrom: null,
  periodTo: null,
  lengthMinKm: null,
  lengthMaxKm: null,
  hasAppliedBarriers: null,
  hasResidualBarriers: null,
  search: '',
};

/** Recently built (or rebuilt) routes surface at the top by default. */
export const DEFAULT_ROUTE_SORT: RouteListSort = {
  field: 'updated_at',
  direction: 'desc',
};

export type RoutesLibraryStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseRoutesLibraryValue {
  readonly items: readonly RouteResponse[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly status: RoutesLibraryStatus;
  readonly errorMessage: string | null;
  readonly filters: RouteListFilters;
  /** Filters actually applied to the current result set (search debounced).
   * Use these — not `filters` — for "select all across pages" so the
   * selected set matches what the table is showing. */
  readonly appliedFilters: RouteListFilters;
  readonly sort: RouteListSort;
  readonly setFilters: (next: RouteListFilters) => void;
  readonly resetFilters: () => void;
  readonly setSort: (next: RouteListSort) => void;
  readonly setPage: (page: number) => void;
  readonly refresh: () => void;
}

export function useRoutesLibrary(): UseRoutesLibraryValue {
  const [filters, setFiltersState] = useState<RouteListFilters>(EMPTY_ROUTE_FILTERS);
  const [sort, setSortState] = useState<RouteListSort>(DEFAULT_ROUTE_SORT);
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<readonly RouteResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<RoutesLibraryStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const { routesRevision } = useTileRevision();
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(filters.search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [filters.search]);

  const queryFilters = useMemo<RouteListFilters>(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );

  useEffect(() => {
    setPage(1);
  }, [
    queryFilters.periodFrom,
    queryFilters.periodTo,
    queryFilters.lengthMinKm,
    queryFilters.lengthMaxKm,
    queryFilters.hasAppliedBarriers,
    queryFilters.hasResidualBarriers,
    queryFilters.search,
    sort.field,
    sort.direction,
  ]);

  useEffect(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setStatus('loading');
    setErrorMessage(null);
    listRoutes({
      limit: DEFAULT_PAGE_SIZE,
      offset: (page - 1) * DEFAULT_PAGE_SIZE,
      periodFrom: queryFilters.periodFrom,
      periodTo: queryFilters.periodTo,
      lengthMinKm: queryFilters.lengthMinKm,
      lengthMaxKm: queryFilters.lengthMaxKm,
      hasAppliedBarriers: queryFilters.hasAppliedBarriers,
      hasResidualBarriers: queryFilters.hasResidualBarriers,
      search: queryFilters.search,
      sortBy: sort.field,
      sortDir: sort.direction,
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        setItems(response.items);
        setTotal(response.total);
        setStatus('success');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || axios.isCancel(error)) {
          return;
        }
        setStatus('error');
        setErrorMessage(extractAxiosMessage(error) ?? 'unknown error');
      });

    return () => controller.abort();
  }, [page, queryFilters, sort, routesRevision, reloadCounter]);

  const setFilters = useCallback((next: RouteListFilters) => {
    setFiltersState(next);
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(EMPTY_ROUTE_FILTERS);
  }, []);

  const setSort = useCallback((next: RouteListSort) => {
    setSortState(next);
  }, []);

  const refresh = useCallback(() => {
    setReloadCounter((value) => value + 1);
  }, []);

  return {
    items,
    total,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status,
    errorMessage,
    filters,
    appliedFilters: queryFilters,
    sort,
    setFilters,
    resetFilters,
    setSort,
    setPage,
    refresh,
  };
}
